import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import useRetryFetch from "./useRetryFetch";
import { useRitualDay } from "./useRitualDay";
import { nowToday } from "../api/now";
import { listTakeaways } from "../api/userTakeaways";
import { listObjects, type MmObject } from "../api/mentalModels";
import { chooseForNow, dismissForNow, readDismissed, type ForNowChoice, type RunningProcess } from "../lib/forNow";

export interface UseForNowArgs {
  running: RunningProcess[];
  runningLoaded: boolean;
  enabled?: boolean;
  now?: () => Date;
}

export interface UseForNow {
  ready: boolean;
  choice: ForNowChoice;
  dismiss: (key: string) => void;
}

const settled = (q: { isFetched: boolean; isError: boolean }) => q.isFetched || q.isError;

/** Gathers For now's inputs from queries Today already uses (same keys, shared cache). */
export function useForNow({ running, runningLoaded, enabled = true, now = () => new Date() }: UseForNowArgs): UseForNow {
  const retryFetch = useRetryFetch();
  const today = useQuery({
    queryKey: ["now-today"],
    queryFn: () => nowToday(retryFetch),
    staleTime: 60_000,
    retry: false,
    enabled,
  });
  const actions = useQuery({
    queryKey: ["home-today-actions"],
    queryFn: () => listTakeaways(retryFetch, { type: "action", status: "pending", limit: 200 }),
    staleTime: 30_000,
    enabled,
  });
  // Same key and query as the Workspace board, so the cache is shared.
  const models = useQuery({
    queryKey: ["workspace", "board", "models"],
    queryFn: () => listObjects(retryFetch, { sort: "attention" }).catch(() => ({ status: "", objects: [] as MmObject[] })),
    staleTime: 60_000,
    enabled,
  });
  const rituals = useRitualDay(enabled);
  const [dismissTick, setDismissTick] = useState(0);

  const at = now();
  const minute = Math.floor(at.getTime() / 60_000);
  const choice = useMemo(
    () =>
      chooseForNow(
        {
          nowToday: today.data ?? null,
          hasCompass: rituals.hasCompass,
          ritualPrefs: rituals.prefs,
          ritualLog: rituals.log,
          running,
          models: models.data?.objects ?? [],
          actions: actions.data?.list ?? [],
          dismissed: readDismissed(at),
        },
        at
      ),
    // `minute` re-runs the chooser as the clock moves; `dismissTick` after a dismissal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [today.data, rituals.hasCompass, rituals.prefs, rituals.log, running, models.data, actions.data, dismissTick, minute]
  );

  const dismiss = useCallback((key: string) => {
    dismissForNow(key, new Date());
    setDismissTick((n) => n + 1);
  }, []);

  const ready = enabled && runningLoaded && settled(today) && settled(actions) && settled(models) && rituals.ready;
  return { ready, choice, dismiss };
}
